interface IFindNode<
  Data extends Record<string, any> = Record<string, any>,
  Target = string | number
> {
  (
    data: Data[],
    target: Target,
    cb?: (isFind: boolean, curNode: Data, parents?: Data[]) => void,
    chains?: Data[],
  ): Data | void
  node?: Data | { prev: Data | null; cur: Data | null }
  isDeep?: boolean
  findName?: keyof Data
  nextPointName?: string
  cacheAble?: boolean
}

/**
 * @summary 查找满足 @params data 中 满足 data[?][findNode.findName] === target的节点， 如根据id找节点，根据name找节点
 * @param { T[] } data 数据源 数组
 * @param { string } target 目标字段的值
 * @param { (isFind: boolean, curNode: Data) => void): Data | void } cb 遍历每一个节点的回调，当第一个参数isFind为true， 表示找到该节点
 */
const findNode: IFindNode = (data, target, cb, chains = []) => {
  findNode.cacheAble = findNode.cacheAble || false
  findNode.findName = findNode.findName || 'id'
  findNode.nextPointName = findNode.nextPointName || 'children'

  if (!findNode.cacheAble) findNode.node = undefined
  else {
    findNode.node = {
      prev: findNode.node?.cur,
      cur: undefined,
    }
  }
  if (!data?.length) {
    chains.pop()
  }
  data?.find((item, index) => {
    const isFind = item[findNode.findName as string] === target
    chains.push({ ...item })
    if (cb) cb(isFind, item, [...chains])
    if (isFind) {
      if (!findNode.isDeep || (findNode.isDeep && !item[findNode.nextPointName!]?.length)) {
        if (!findNode.cacheAble) {
          findNode.node = item
          return findNode.node || item
        }
        findNode.node!.cur = findNode.node
        return findNode.node?.cur
      }
      if (findNode.isDeep && item[findNode.nextPointName!]?.length) {
        return findNode(item[findNode.nextPointName!], target, cb, chains) || item
      }
    }
    if (index === data.length - 1) {
      chains.pop()
    }
    return findNode(item[findNode.nextPointName!], target, cb, chains)
  })
  return findNode.cacheAble ? findNode.node?.cur : findNode.node
}

export type IAliasFieldType<OriginalType, AliasType> = Record<
  keyof OriginalType | string,
  | string
  | string[]
  | ((
      data: OriginalType & Partial<AliasType>,
      parent?: Partial<OriginalType> & Partial<AliasType>,
    ) => any)
>

/**
 * @summary 适用于给父子数据源同种结构内指定字段起别名
 * @param { Record<string, any>[] } source 数据源
 * @param { Record<string, string> } aliasFields 需要起别名的字段映射表, { originalFieldName: targetFieldName }
 * @param { isClone } boolean 是否复制一份新的数据出来，如 isClone = true: {id, value} => { key, title } | isClone = false: { id, value } => { id, value, key, title }
 */
const aliasArrayStructureFieldName = <
  OriginalType extends Record<string, any>,
  AliasType = Record<string, any>
>(
  source: OriginalType[],
  aliasFields: IAliasFieldType<OriginalType, AliasType>,
  isClone?: boolean,
  parent?: Partial<OriginalType> & Partial<AliasType>,
) => {
  aliasArrayStructureFieldName.nextPointName =
    aliasArrayStructureFieldName.nextPointName || 'children'

  const nextPointName =
    aliasFields[aliasArrayStructureFieldName.nextPointName as string] ||
    (aliasArrayStructureFieldName.nextPointName as string)

  // @ts-ignore
  aliasFields[nextPointName as string] = 'children'

  const result = source?.[isClone ? 'map' : 'forEach']((item: Record<string, any>) => {
    const data = isClone ? { ...item } : item
    Object.entries(aliasFields).forEach(([originalName, targetName]) => {
      if (typeof targetName === 'string') {
        // eslint-disable-next-line no-param-reassign
        data[targetName] = data[originalName]
      } else if (typeof targetName === 'function') {
        const value = targetName(
          data as OriginalType & Partial<AliasType>,
          parent as Partial<OriginalType> & Partial<AliasType>,
        )
        data[originalName] = value
      } else if (targetName instanceof Array) {
        ;(targetName as string[]).forEach((target) => {
          data[target] = data[originalName]
        })
      }
      if (isClone) Reflect.deleteProperty(data, originalName)
    })
    if (data[nextPointName as string]) {
      if (isClone) {
        data[nextPointName as string] = aliasArrayStructureFieldName(
          data[nextPointName as string],
          aliasFields,
          isClone,
        )
      } else {
        aliasArrayStructureFieldName(
          data[nextPointName as string],
          aliasFields,
          isClone,
          (data as unknown) as OriginalType & Partial<AliasType>,
        )
      }
    }
    return data
  })
  return (isClone ? result : source) as (OriginalType & AliasType)[]
}
aliasArrayStructureFieldName.nextPointName = ''

const filterNode = <SourceType extends Record<string, any>>(
  data: SourceType[],
  condition: (item: SourceType) => SourceType | boolean,
) => {
  filterNode.nextPointName = filterNode.nextPointName || 'children'
  return data
    .map((item: Record<string, any>) => {
      const tmpNode = { ...item }
      if (tmpNode[filterNode.nextPointName]?.length) {
        // eslint-disable-next-line no-param-reassign
        tmpNode[filterNode.nextPointName] = filterNode<SourceType>(
          tmpNode[filterNode.nextPointName],
          condition,
        )
      }
      const result = condition(<SourceType>tmpNode)
      if (result) {
        return tmpNode
      }
      return null
    })
    .filter((v) => v) as SourceType[]
}
filterNode.nextPointName = ''

export { findNode, aliasArrayStructureFieldName, filterNode }
